import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  Portfolio,
  PortfolioState,
  PortfolioResponse,
  AllPortfoliosResponse,
  CreatePortfolioRequest,
  UpdatePortfolioRequest,
  BaseResponse,
} from "./types";
import { getCookie } from "../../utility/getCookie";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_CUSTOMER_APP_BACKEND_PREFIX,
]
  .filter(Boolean)
  .join("");

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    let token = getCookie("access_token");
    if (token === null || token === undefined) {
      try {
        token = getCookie("access_token");
      } catch (err) {
        // Handle refresh failure (e.g., redirect to login)
      }
    }
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const createPortfolio = createAsyncThunk(
  "portfolio/create",
  async (data: CreatePortfolioRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<PortfolioResponse>("/portfolio", data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create portfolio",
      );
    }
  },
);

export const updatePortfolio = createAsyncThunk(
  "portfolio/update",
  async (data: UpdatePortfolioRequest, { rejectWithValue }) => {
    try {
      const response = await api.put<PortfolioResponse>(
        "/portfolio/update",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update portfolio",
      );
    }
  },
);

export const deletePortfolio = createAsyncThunk(
  "portfolio/delete",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete<BaseResponse>("/portfolio/delete");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete portfolio",
      );
    }
  },
);

export const getMyPortfolio = createAsyncThunk(
  "portfolio/getMyPortfolio",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(
        "/portfolio/my-portfolio",
      );
      console.log(response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch your portfolio",
      );
    }
  },
);

export const getPortfolioById = createAsyncThunk(
  "portfolio/getById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get<PortfolioResponse>(`/portfolio/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch portfolio",
      );
    }
  },
);

export const getAllPortfolios = createAsyncThunk(
  "portfolio/getAll",
  async (params: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await api.get<AllPortfoliosResponse>("/portfolio", {
        params,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch portfolios",
      );
    }
  },
);

export const followPortfolio = createAsyncThunk(
  "portfolio/follow",
  async (portfolioId: string, { rejectWithValue }) => {
    try {
      const response = await api.post<BaseResponse>(
        `/portfolio/follow/${portfolioId}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to follow portfolio",
      );
    }
  },
);

export const unfollowPortfolio = createAsyncThunk(
  "portfolio/unfollow",
  async (portfolioId: string, { rejectWithValue }) => {
    try {
      const response = await api.post<BaseResponse>(
        `/portfolio/unfollow/${portfolioId}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to unfollow portfolio",
      );
    }
  },
);

const initialState: PortfolioState = {
  portfolio: null,
  myPortfolio: null,
  allPortfolios: [],
  loading: false,
  error: null,
  success: false,
  message: null,
  totalCount: 0,
};

const portfolioSlice = createSlice({
  name: "portfolio",
  initialState,
  reducers: {
    clearState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Portfolio
      .addCase(createPortfolio.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPortfolio.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.portfolio = action.payload.data;
        state.message = action.payload.message;
      })
      .addCase(createPortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Portfolio
      .addCase(updatePortfolio.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePortfolio.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.portfolio = action.payload.data;
        state.myPortfolio = action.payload.data;
        state.message = action.payload.message;
      })
      .addCase(updatePortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete Portfolio
      .addCase(deletePortfolio.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePortfolio.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.portfolio = null;
        state.myPortfolio = null;
        state.message = action.payload.message;
      })
      .addCase(deletePortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get My Portfolio
      .addCase(getMyPortfolio.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMyPortfolio.fulfilled, (state, action) => {
        state.loading = false;
        state.myPortfolio = action.payload;
        state.portfolio = action.payload;
      })
      .addCase(getMyPortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get Portfolio By ID
      .addCase(getPortfolioById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPortfolioById.fulfilled, (state, action) => {
        state.loading = false;
        state.portfolio = action.payload.data;
      })
      .addCase(getPortfolioById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get All Portfolios
      .addCase(getAllPortfolios.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllPortfolios.fulfilled, (state, action) => {
        state.loading = false;
        state.allPortfolios = action.payload.data;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(getAllPortfolios.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Follow Portfolio
      .addCase(followPortfolio.pending, (state) => {
        state.loading = true;
      })
      .addCase(followPortfolio.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(followPortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Unfollow Portfolio
      .addCase(unfollowPortfolio.pending, (state) => {
        state.loading = true;
      })
      .addCase(unfollowPortfolio.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(unfollowPortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearState } = portfolioSlice.actions;
export default portfolioSlice.reducer;
